import React, { useState } from 'react';
import { ChevronRight, ChevronDown, AlertTriangle } from 'lucide-react';
import type { ValidationQueueEntry, TaxonomyEntry, ReviewStatus } from '../../types';
import { CategoryCombobox } from './CategoryCombobox';

// Stable empty set used as the default for hiddenColumns so callers that
// don't supply the prop avoid a new Set() allocation on every render.
const EMPTY_SET = new Set<string>();

interface ValidationRowProps {
  entry: ValidationQueueEntry;
  rowIndex: number;
  /** The pending decision from the local edit map (undefined = not yet touched) */
  pendingDecision: string | null | undefined;
  taxonomy: TaxonomyEntry[];
  onDecisionChange: (partId: string, selectedCategoryId: string | null) => void;
  /** Set of column keys that should not be rendered (from ValidationGrid state) */
  hiddenColumns?: ReadonlySet<string>;
}

/**
 * Classify a row for the Status column and for row styling.
 *
 * Mirrors `review_status()` in backend/apps/processing/views.py — keep the two
 * in step. The server's answer is authoritative and arrives as
 * `entry.review_status`; this runs only when the reviewer has an *unsaved*
 * decision, so the badge updates on click instead of after the round-trip.
 *
 * Decision: PDR-054 — a stored decision counts as handled. This previously
 * required the value to DIFFER from ai_category_1_id, so the rows written by
 * "Approve All" (which stores the AI's own suggestion) stayed unhighlighted
 * while the server had already stopped returning them from ?state=unreviewed.
 * See docs/decisions/PDR-054-validation-review-status-is-ranked-by-urgency.md
 */
function deriveReviewStatus(
  entry: ValidationQueueEntry,
  pendingDecision: string | null | undefined,
): ReviewStatus {
  // A pending (unsaved) decision wins; otherwise trust the server, falling back
  // to the stored value for rows the server has not classified.
  if (pendingDecision === undefined && entry.review_status) return entry.review_status;

  const effective =
    pendingDecision !== undefined ? pendingDecision : entry.selected_category_id;

  if (effective === '') return 'excluded';
  if (effective !== null) return 'edited';
  return entry.ai_category_1_unresolved === true ? 'flagged' : 'unreviewed';
}

/** User-facing label per state (CLAUDE.md §10 — matches the filter chips). */
const STATUS_LABEL: Record<ReviewStatus, string> = {
  flagged: 'Flagged',
  unreviewed: 'Unreviewed',
  edited: 'Edited',
  excluded: 'Excluded',
};

const STATUS_STYLE: Record<ReviewStatus, string> = {
  flagged:
    'bg-qc-error/10 text-qc-error',
  unreviewed:
    'bg-bg-subtle text-text-secondary',
  edited:
    'bg-interactive-primary/10 text-interactive-primary',
  excluded:
    'bg-bg-subtle text-text-secondary',
};

export const ValidationRow: React.FC<ValidationRowProps> = ({
  entry,
  rowIndex,
  pendingDecision,
  taxonomy,
  onDecisionChange,
  hiddenColumns = EMPTY_SET,
}) => {
  const [expanded, setExpanded] = useState(false);

  const reviewStatus = deriveReviewStatus(entry, pendingDecision);
  // 'flagged' is a sub-state of unreviewed — it colours the badge, not the row.
  const rowState = reviewStatus === 'flagged' ? 'unreviewed' : reviewStatus;

  // Current combobox value: pending decision overrides stored value; fall back to ai_category_1_id
  const comboboxValue: string | null =
    pendingDecision !== undefined
      ? pendingDecision
      : entry.selected_category_id ?? entry.ai_category_1_id ?? null;

  // Unresolved = AI proposed a category name that wasn't in the taxonomy AND the
  // reviewer hasn't filled in the Final Category yet.
  const isFlagged = reviewStatus === 'flagged';

  const rowBg =
    rowState === 'edited'
      ? 'bg-interactive-primary/10'
      : rowState === 'excluded'
      ? 'bg-qc-error/10'
      : '';

  return (
    <tr
      data-testid={`validation-row-${entry.part_number}`}
      className={`border-b border-border hover:bg-bg-subtle/50 transition-colors ${rowBg}`}
    >
      {/* # — always visible */}
      <td className="px-2 py-2 text-xs text-text-tertiary text-right w-10">
        {rowIndex + 1}
      </td>

      {/* Status — sortable by urgency (PDR-054) */}
      {!hiddenColumns.has('status') && (
        <td className="px-2 py-2 w-28">
          <span
            data-testid={`review-status-${entry.part_number}`}
            className={`inline-block px-2 py-0.5 text-xs font-medium rounded-full whitespace-nowrap ${STATUS_STYLE[reviewStatus]}`}
          >
            {STATUS_LABEL[reviewStatus]}
          </span>
        </td>
      )}

      {/* Part Number */}
      {!hiddenColumns.has('part_number') && (
        <td className="px-2 py-2 text-xs font-mono text-text-primary w-36">
          {entry.part_number}
        </td>
      )}

      {/* Part Name */}
      {!hiddenColumns.has('part_name') && (
        <td className="px-2 py-2 text-xs text-text-primary w-48">
          <span title={entry.part_name} className="block truncate max-w-[180px]">
            {entry.part_name}
          </span>
        </td>
      )}

      {/* Description — expandable */}
      {!hiddenColumns.has('description') && (
        <td className="px-2 py-2 text-xs text-text-secondary w-72">
          <div>
            <div className="flex items-start gap-1">
              <button
                type="button"
                onClick={() => setExpanded((e) => !e)}
                data-testid={`expand-description-${entry.part_number}`}
                aria-label={expanded ? 'Collapse description' : 'Expand description'}
                className="mt-0.5 shrink-0 text-text-tertiary hover:text-text-secondary"
              >
                {expanded ? (
                  <ChevronDown className="w-3.5 h-3.5" />
                ) : (
                  <ChevronRight className="w-3.5 h-3.5" />
                )}
              </button>
              <span className={expanded ? '' : 'truncate max-w-[220px]'}>
                {entry.short_description}
              </span>
            </div>
            {expanded && entry.long_description && (
              <p className="mt-1 pl-5 text-text-tertiary whitespace-pre-wrap">
                {entry.long_description}
              </p>
            )}
          </div>
        </td>
      )}

      {/* Current Category */}
      {!hiddenColumns.has('current_category') && (
        <td className="px-2 py-2 text-xs text-text-tertiary w-44">
          {entry.current_category_name ?? (
            <span className="text-text-tertiary">—</span>
          )}
        </td>
      )}

      {/* AI Category 1 — click to quick-select, or show unresolved warning */}
      {!hiddenColumns.has('ai_category_1') && (
        <td className="px-2 py-1.5 text-xs w-48">
          {entry.ai_category_1_id ? (
            <button
              type="button"
              onClick={() => onDecisionChange(entry.part_id, entry.ai_category_1_id!)}
              title="Click to select as final category"
              className={`w-full text-left px-1.5 py-1 rounded transition-colors truncate block max-w-[180px] ${
                comboboxValue === entry.ai_category_1_id
                  ? 'bg-interactive-primary/10 text-interactive-primary ring-1 ring-inset ring-interactive-primary'
                  : 'text-text-secondary hover:bg-interactive-primary/20 hover:text-interactive-primary-hover'
              }`}
            >
              {entry.ai_category_1_name}
            </button>
          ) : entry.ai_category_1_unresolved ? (
            <div className="space-y-1 px-0.5">
              {/* AI-proposed name that wasn't found in the taxonomy */}
              <div className="flex items-center gap-1">
                <AlertTriangle className="w-3 h-3 text-qc-warning shrink-0" aria-hidden />
                <span
                  title={entry.ai_category_1_name}
                  className="truncate max-w-[160px] text-qc-warning"
                >
                  {entry.ai_category_1_name}
                </span>
              </div>
              {/* Fuzzy suggestion — only shown when both cat1 and cat2 are unresolved */}
              {entry.ai_category_1_fuzzy_id && (
                <button
                  type="button"
                  data-testid={`fuzzy-suggest-${entry.part_number}`}
                  onClick={() => onDecisionChange(entry.part_id, entry.ai_category_1_fuzzy_id!)}
                  title={`Fuzzy match (${entry.ai_category_1_fuzzy_score ?? 0}% similarity) — click to use as final category`}
                  className="block truncate max-w-[180px] text-left text-interactive-primary hover:underline"
                >
                  → {entry.ai_category_1_fuzzy_name}
                </button>
              )}
            </div>
          ) : (
            <span className="text-text-tertiary">—</span>
          )}
        </td>
      )}

      {/* AI Category 2 — click to quick-select */}
      {!hiddenColumns.has('ai_category_2') && (
        <td className="px-2 py-1.5 text-xs w-48">
          {entry.ai_category_2_id ? (
            <button
              type="button"
              onClick={() => onDecisionChange(entry.part_id, entry.ai_category_2_id!)}
              title="Click to select as final category"
              className={`w-full text-left px-1.5 py-1 rounded transition-colors truncate block max-w-[180px] ${
                comboboxValue === entry.ai_category_2_id
                  ? 'bg-interactive-primary/10 text-interactive-primary ring-1 ring-inset ring-interactive-primary'
                  : 'text-text-tertiary hover:bg-interactive-primary/20 hover:text-interactive-primary-hover'
              }`}
            >
              {entry.ai_category_2_name}
            </button>
          ) : (
            <span className="px-1.5 text-text-tertiary">—</span>
          )}
        </td>
      )}

      {/* AI Specific (Category 3) */}
      {!hiddenColumns.has('ai_specific') && (
        <td className="px-2 py-2 text-xs text-text-tertiary w-48">
          <span title={entry.ai_category_3 ?? ''} className="block truncate max-w-[180px]">
            {entry.ai_category_3 ?? (
              <span className="text-text-tertiary">—</span>
            )}
          </span>
        </td>
      )}

      {/* Final Category — always visible, editable combobox */}
      {/* Red ring when the row is flagged and the reviewer hasn't picked a category yet */}
      <td className="px-2 py-2 w-60">
        <div className={isFlagged && comboboxValue === null ? 'ring-2 ring-qc-error rounded-lg' : ''}>
          <CategoryCombobox
            partNumber={entry.part_number}
            value={comboboxValue}
            taxonomy={taxonomy}
            onChange={(val) => onDecisionChange(entry.part_id, val)}
          />
        </div>
      </td>
    </tr>
  );
};
